import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useStore } from "@nanostores/react";
import { fetchPendingTasks } from "@vega-api/custom/lp-tasks/fetchPendingTasks";
import { $txId, clearTxId } from "@vega-api/custom/stores/txId.store";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { getGetTasksQueryKey } from "@vega-api/generated/lp-tasks/lp-tasks";
import type { GetLpTasksResponse } from "@vega-api/generated/types/getLpTasksResponse";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { LocalStorageService } from "@vega-core/service/LocalStorage.service";

import { type PollOutcome, pollWithRetry } from "./pollWithRetry";

export const PENDING_TASKS_MAX_ATTEMPTS = 10;
export const PENDING_TASKS_RETRY_DELAY_MS = 100;

const EMPTY_TASKS_RESPONSE: GetLpTasksResponse = {
  data: [],
  meta: null,
  errors: {},
};

export const usePendingTasks = () => {
  const showToast = useToast();
  const queryClient = useQueryClient();
  const { featureFlag } = useFeatureFlag();
  const flagOn = Boolean(featureFlag[FeatureFlag.LP_AUTH_LOGIN_TASK]);
  const txId = useStore($txId);

  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  useEffect(() => {
    if (!flagOn) return;

    if (queryClient.getQueryData(getGetTasksQueryKey()) !== undefined) {
      return;
    }

    const seedEmptyCache = () => {
      queryClient.setQueryData(getGetTasksQueryKey(), EMPTY_TASKS_RESPONSE);
    };

    const fail = () => {
      showToastRef.current({
        variant: "destructive",
        title: m.pendingTasks_loadError(),
      });
    };

    // Tasks already completed this session: an empty list is correct and no
    // txId is needed (tasksState is cleared on logout, so a fresh login
    // re-fetches).
    if (LocalStorageService.getLpTasksCompleted()) {
      seedEmptyCache();

      return;
    }

    // No txId yet and tasks aren't completed: wait for useTxIdCapture to either
    // mint one (this effect re-runs) or force a logout. We must NOT seed an
    // empty list here without ever calling /tasks — that is the reload bypass.
    if (txId === null) {
      return;
    }

    return pollWithRetry({
      maxAttempts: PENDING_TASKS_MAX_ATTEMPTS,
      delayMs: PENDING_TASKS_RETRY_DELAY_MS,
      run: async ({ signal }): Promise<PollOutcome> => {
        try {
          const result = await fetchPendingTasks(txId, signal);

          if (result.status === 200) {
            queryClient.setQueryData(getGetTasksQueryKey(), result.data);
            clearTxId();

            return "done";
          }

          return "retry";
        } catch {
          if (signal.aborted) return "stop";
          // The /tasks endpoint errored despite a valid txId. Surface it and
          // let the LP proceed rather than locking everyone out during an
          // outage — the reload bypass is closed by the no-txId branch above.
          seedEmptyCache();
          fail();

          return "stop";
        }
      },
      onExhausted: () => {
        seedEmptyCache();
        fail();
      },
    });
  }, [flagOn, queryClient, txId]);
};
